var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Skeleton, } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { PageContainer, PageContent } from "../../../components/wraper";
import PageHeader from "@ui/components/header/PageHeader";
import { FCard } from "@ui/components/card";
import { FSelect } from "@ui/components/inputs/FSelect";
import { FInput } from "@ui/components/inputs/FInput";
import { StatusIndicator } from "@ui/components/status";
import { TemperatureDisplay } from "@ui/components/temperature/TemperatureDisplay";
import { alarmNotificationsApi, } from "@api/enpoints/alarm-notifications-api";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import { formatUtcToLocalDateTimeVerbose } from "@application/common/helpers/date-util";
import { isHighAlarmType, isLowAlarmType } from "@ui/utils/alarm-type-label";
import { sensorLabelKey } from "@ui/utils/sensors";
const getAlarmTypeName = (alarmType, t, sensorType) => {
    switch (alarmType) {
        case 1: return "Low Temperature";
        case 2: return "High Temperature";
        case 3: return "Fat Replacement";
        case 4: return "Open Door";
        case 5: return "No Node Communication";
        case 6: return "No AP Communication";
        case 7: return "Temperature Reading Delay";
        case 8:
        case 9: {
            const key = sensorLabelKey(sensorType);
            if (!t)
                return alarmType === 8 ? "Sensor Limit Low" : "Sensor Limit High";
            return t(alarmType === 8 ? "common:alarmTypes.lowSensorLimit" : "common:alarmTypes.highSensorLimit", { sensor: key ? t(key) : t("common:alarmTypes.unknown") });
        }
        default: return "Unknown";
    }
};
const formatDateTime = (dateString) => {
    try {
        return formatUtcToLocalDateTimeVerbose(dateString) || dateString;
    }
    catch (_a) {
        return dateString;
    }
};
export const EditAcknowledgedAlarmPage = () => {
    var _a, _b, _c;
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const { alarmId } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [alarm, setAlarm] = useState(null);
    const [causeOptions, setCauseOptions] = useState([
        { value: "", label: t("common:actions.selectACause") },
    ]);
    const { control, handleSubmit, watch, reset, formState: { errors }, } = useForm({
        defaultValues: {
            cause: "",
            explanation: "",
        },
    });
    const watchCause = watch("cause");
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!alarmId) {
                toast.error(t("common:toasts.alarmIdNotProvided"));
                navigate("/alarms");
                return;
            }
            setLoading(true);
            try {
                const [alarmResponse, causesResponse] = yield Promise.all([
                    alarmNotificationsApi.getAlarmById(alarmId),
                    alarmNotificationsApi.getAlarmCausesDropdown(),
                ]);
                if (!alarmResponse.isSuccess || !alarmResponse.data) {
                    toast.error(t("common:toasts.failedToFetchAlarmDetails"));
                    navigate("/alarms");
                    return;
                }
                const fetchedAlarm = alarmResponse.data;
                if (!fetchedAlarm.isAlarmAcknowledged) {
                    toast.error(t("common:toasts.alarmNotAcknowledged"));
                    navigate("/alarms");
                    return;
                }
                setAlarm(fetchedAlarm);
                if (causesResponse.isSuccess && causesResponse.data) {
                    const options = [
                        { value: "", label: t("common:actions.selectACause") },
                        ...causesResponse.data.map((c) => ({
                            value: c.value,
                            label: c.label,
                        })),
                    ];
                    setCauseOptions(options);
                }
                reset({
                    cause: fetchedAlarm.acknowledgeCauseId || "",
                    explanation: fetchedAlarm.explanation || "",
                });
            }
            catch (error) {
                console.error("Error fetching alarm data:", error);
                toast.error(t("common:toasts.errorLoadingAlarmData"));
                navigate("/alarms");
            }
            finally {
                setLoading(false);
            }
        });
        fetchData();
    }, [alarmId, navigate, reset, t]);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!alarmId)
            return;
        if (!data.cause) {
            toast.error(t("common:toasts.pleaseSelectCause"));
            return;
        }
        setSubmitting(true);
        try {
            const response = yield alarmNotificationsApi.editAcknowledgedAlarm(alarmId, {
                causeId: data.cause,
                explanation: data.explanation || undefined,
            });
            if (response.isSuccess) {
                toast.success(t("common:toasts.alarmUpdatedSuccessfully"));
                navigate("/alarms");
            }
            else {
                toast.error(response.message || t("common:toasts.failedToUpdateAlarm"));
            }
        }
        catch (error) {
            console.error("Error updating alarm:", error);
            toast.error(t("common:toasts.errorUpdatingAlarm"));
        }
        finally {
            setSubmitting(false);
        }
    });
    const actionButton = {
        label: submitting
            ? t("common:actions.updating")
            : t("common:actions.saveChanges"),
        onClick: handleSubmit(onSubmit),
        variant: "contained",
        size: "large",
        disabled: submitting || !watchCause,
    };
    if (loading) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("common:pages.editAcknowledgedAlarm"), showBackButton: true }),
            React.createElement(PageContent, null,
                React.createElement(Grid, { container: true, spacing: 1 },
                    React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                        React.createElement(Skeleton, { variant: "rounded", height: 180 })),
                    React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                        React.createElement(Grid, { container: true, spacing: 1 }, [...Array(4)].map((_, i) => (React.createElement(Grid, { key: i, size: { xs: 12, sm: 12, md: 6 } },
                            React.createElement(Skeleton, { variant: "rounded", height: 85 }))))))),
                React.createElement(Box, { mt: 3 },
                    React.createElement(Grid, { container: true, spacing: 1 },
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(Skeleton, { variant: "rounded", height: 56 })),
                        React.createElement(Grid, { size: { xs: 12 } },
                            React.createElement(Skeleton, { variant: "rounded", height: 120 })))))));
    }
    if (!alarm) {
        return (React.createElement(PageContainer, null,
            React.createElement(PageHeader, { title: t("common:pages.editAcknowledgedAlarm"), showBackButton: true }),
            React.createElement(PageContent, null,
                React.createElement(Typography, null, t("common:actions.alarmNotFound")))));
    }
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.editAcknowledgedAlarm"), showBackButton: true, bottomActionButton: actionButton, topActions: React.createElement(StatusIndicator, { variant: "acknowledged" }) }),
        React.createElement(PageContent, { hasBottomAction: true },
            React.createElement(Grid, { container: true, spacing: 1 },
                React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                    React.createElement(FCard, null,
                        React.createElement(Box, { p: 1.8, sx: {
                                display: "flex",
                                gap: 1,
                                flexDirection: { md: "row", lg: "column" },
                            } },
                            React.createElement(Box, { sx: { mb: 2 } },
                                React.createElement(TemperatureDisplay, { temperature: (_a = alarm.temperature) !== null && _a !== void 0 ? _a : 0, leftIndicator: isLowAlarmType(alarm.alarmType) ? alarm.threshold : undefined, rightIndicator: isHighAlarmType(alarm.alarmType) ? alarm.threshold : undefined, width: 91 })),
                            React.createElement(Box, { sx: { minWidth: 0 } },
                                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 0.5 } }, "Name"),
                                React.createElement(Typography, { noWrap: true, variant: "body1", sx: { fontWeight: 500, mb: 0.5, minWidth: 0 } }, alarm.nodeName || alarm.nodeUid),
                                React.createElement(Typography, { noWrap: true, variant: "caption", color: "text.secondary", sx: { minWidth: 0 } }, alarm.accessPointName || alarm.accessPointUid))))),
                React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                    React.createElement(Grid, { container: true, spacing: 1 },
                        React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                            React.createElement(FCard, { variant: "stats", title: t("common:labels.date"), value: formatDateTime(alarm.alarmStartDateTime) })),
                        React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                            React.createElement(FCard, { variant: "stats", title: t("common:labels.alarmType"), value: getAlarmTypeName(alarm.alarmType, t, alarm.sensorType) })),
                        React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                            React.createElement(FCard, { variant: "stats", title: t("common:labels.valueLimitValue"), value: `${(_c = (_b = alarm.temperature) === null || _b === void 0 ? void 0 : _b.toFixed(1)) !== null && _c !== void 0 ? _c : "N/A"} (${alarm.threshold})` })),
                        React.createElement(Grid, { size: { xs: 12, sm: 12, md: 6 } },
                            React.createElement(FCard, { variant: "stats", title: t("common:labels.handlingThrough"), value: alarm.acknowledgedByName || "N/A" }))))),
            React.createElement(Box, { component: "form", mt: 3, onSubmit: handleSubmit(onSubmit) },
                React.createElement(Grid, { container: true, spacing: 1 },
                    React.createElement(Grid, { size: { xs: 12 } },
                        React.createElement(FSelect, { name: "cause", control: control, label: t("common:labels.alarmCause"), options: causeOptions, required: true, rules: {
                                required: t("common:toasts.pleaseSelectCause"),
                                validate: (value) => (typeof value === "string" && value !== "") ||
                                    t("common:toasts.pleaseSelectCause"),
                            } })),
                    React.createElement(Grid, { size: { xs: 12 } },
                        React.createElement(FInput, { name: "explanation", control: control, label: t("common:labels.explanation"), placeholder: t("common:placeholders.pleaseExplainCause"), multiline: true, rows: 4 })))))));
};
